import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  ISignatureComponent,
  IFormControlChange,
  IFormValidationErrors
} from '../df-form-preview-interfaces';
import { DfFormErrorMsg } from '../df-form-error-msg';

interface DfFormSignatureProps {
  id: string;
  properties: ISignatureComponent;
  validationErrors?: IFormValidationErrors;
  formValue?: string;
  readonly?: boolean;
  disabled?: boolean;
  touchedFields?: Record<string, boolean>;
  formSubmitted?: boolean;
  mode?: 'edit' | 'preview' | 'test';
  onValueChange?: (change: IFormControlChange) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  className?: string;
  hideLabel?: boolean;
}

export const DfFormSignature: React.FC<DfFormSignatureProps> = ({
  id,
  properties,
  validationErrors = {},
  formValue = '',
  readonly = false,
  disabled = false,
  touchedFields = {},
  formSubmitted = false,
  mode = 'preview',
  onValueChange,
  onBlur,
  onFocus,
  className = '',
  hideLabel = false
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState<boolean>(false);
  const [isTouched, setIsTouched] = useState<boolean>(false);
  const [signatureData, setSignatureData] = useState<string>(formValue);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  // Validate field
  const validateField = useCallback((val: string): { isValid: boolean; errors: Record<string, any> } => {
    const errors: Record<string, any> = {};
    let isValid = true;

    // Required validation
    if (properties?.validation?.required && (!val || val.trim() === '')) {
      errors.required = true;
      isValid = false;
    }

    return { isValid, errors };
  }, [properties]);

  // Handle value changes
  const handleValueChange = useCallback((newValue: string) => {
    setSignatureData(newValue);
    
    if (onValueChange) {
      const validation = validateField(newValue);
      const change: IFormControlChange = {
        id,
        value: newValue,
        isValid: validation.isValid,
        errors: validation.errors,
      };
      onValueChange(change);
    }
  }, [id, onValueChange, validateField]);

  // Get theme-aware colors
  const getThemeColors = useCallback(() => {
    const isDarkMode = document.documentElement.classList.contains('dark') || 
                      document.documentElement.getAttribute('data-theme') === 'dark';
    
    // Get CSS variable values
    const computedStyle = getComputedStyle(document.documentElement);
    let backgroundColor = computedStyle.getPropertyValue('--df-color-fb-input-bg').trim();
    
    // If CSS variable is not available or empty, use fallback
    if (!backgroundColor) {
      backgroundColor = isDarkMode ? '#2c2e34' : '#ffffff';
    }
    
    const strokeColor = isDarkMode ? '#ffffff' : '#000000';
    
    return {
      strokeColor,
      backgroundColor
    };
  }, []);

  // Load signature from data URL
  const loadSignature = useCallback((dataURL: string) => {
    if (!dataURL) { 
      return;
    }
    
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }
    
    const img = new Image();
    img.onload = () => {
      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Set background
      const colors = getThemeColors();
      ctx.fillStyle = colors.backgroundColor;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Calculate scaling to fit the signature properly
      const canvasAspectRatio = canvas.width / canvas.height;
      const imgAspectRatio = img.width / img.height;
      
      let drawWidth = canvas.width;
      let drawHeight = canvas.height;
      let drawX = 0;
      let drawY = 0;
      
      if (imgAspectRatio > canvasAspectRatio) {
        // Image is wider than canvas
        drawHeight = canvas.width / imgAspectRatio;
        drawY = (canvas.height - drawHeight) / 2;
      } else {
        // Image is taller than canvas
        drawWidth = canvas.height * imgAspectRatio;
        drawX = (canvas.width - drawWidth) / 2;
      }
      
      // Draw the signature image with proper scaling
      ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
    };
    
    img.onerror = (error) => {
      console.error('DfFormSignature: error loading image', {
        id,
        error,
        dataURL: dataURL.substring(0, 50) + '...'
      });
    };
    
    img.src = dataURL;
  }, [getThemeColors, id]);

  // Initialize canvas
  const initializeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }
    
    // Get the display size
    const rect = canvas.getBoundingClientRect();
    
    // Set canvas size to match display size
    canvas.width = rect.width;
    canvas.height = rect.height;
    
    // Ensure canvas is properly sized
    canvas.style.width = rect.width + 'px';
    canvas.style.height = rect.height + 'px';
    
    const colors = getThemeColors();
    
    // Set drawing styles
    ctx.strokeStyle = colors.strokeColor;
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    
    // Set background only if canvas is empty
    if (!signatureData) {
      ctx.fillStyle = colors.backgroundColor;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    } else {
      loadSignature(signatureData);
    }
  }, [signatureData, getThemeColors, loadSignature]);

  // Handle viewport changes and device view switches
  const handleViewportChange = useCallback(() => {
    // Small delay to ensure DOM has updated after viewport change
    setTimeout(() => {
      initializeCanvas();
    }, 100);
  }, [initializeCanvas]);

  // Listen for viewport changes and device view switches
  useEffect(() => {
    // Listen for window resize events
    const handleResize = () => {
      handleViewportChange();
    };

    // Listen for device view changes by observing class changes on the form canvas container
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && 
            (mutation.attributeName === 'class')) {
          const target = mutation.target as HTMLElement;
          // Check if the class change is related to device view switching
          if (target.classList.contains('form-canvas-container') && 
              (target.classList.contains('mobile-view') || 
               target.classList.contains('tablet-view') || 
               target.classList.contains('desktop-view'))) {
            handleViewportChange();
          }
        }
      });
    });

    // Start observing the document for class changes
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
      subtree: true
    });

    // Add resize listener
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, [handleViewportChange]);


  // Get accurate coordinates relative to canvas
  const getCanvasCoordinates = useCallback((e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    const clientX = e.type.includes('mouse') 
      ? (e as React.MouseEvent).clientX
      : (e as React.TouchEvent).touches[0].clientX;
    const clientY = e.type.includes('mouse')
      ? (e as React.MouseEvent).clientY
      : (e as React.TouchEvent).touches[0].clientY;
    
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY
    };
  }, []);

  // Start drawing
  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (readonly || disabled || properties?.validation?.readonly) return;
    
    // Check if the click is on the clear button area
    const target = e.target as HTMLElement;
    if (target.closest('.clear-signature-btn')) {
      return; // Don't start drawing if clicking on the clear button
    }
    
    e.preventDefault();
    e.stopPropagation();
    
    setIsDrawing(true);
    setIsTouched(true);
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    const { x, y } = getCanvasCoordinates(e);
    
    // Set drawing styles
    const colors = getThemeColors();
    ctx.strokeStyle = colors.strokeColor;
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  // Draw
  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }
    
    const { x, y } = getCanvasCoordinates(e);

    ctx.lineTo(x, y);
    ctx.stroke();
  };

  // Check if canvas has content
  const hasCanvasContent = (canvas: HTMLCanvasElement): boolean => {
    const ctx = canvas.getContext('2d');
    if (!ctx) return false;
    
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
    
    // Check if any pixel is not white (has content)
    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const a = data[i + 3];
      
      // If pixel is not white or transparent, canvas has content
      if (a > 0 && (r < 255 || g < 255 || b < 255)) {
        return true;
      }
    }
    return false;
  };

  // Stop drawing
  const stopDrawing = () => {
    if (!isDrawing) return;
    
    setIsDrawing(false);
    setIsSaving(true); // Set saving flag
    
    // Convert canvas to data URL
    const canvas = canvasRef.current;
    if (canvas) {
      // Check if canvas has content before saving
      hasCanvasContent(canvas);
      
      const dataURL = canvas.toDataURL('image/png');
      
      setSignatureData(dataURL);
      handleValueChange(dataURL);
      
      // Clear saving flag after a short delay
      setTimeout(() => {
        setIsSaving(false);
      }, 100);
    } else {
      setIsSaving(false);
    }
  };

  // Clear signature
  const clearSignature = (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();

    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    
    // Get the display dimensions
    const rect = canvas.getBoundingClientRect();
    
    // Reset canvas dimensions to force a complete clear
    canvas.width = rect.width;
    canvas.height = rect.height;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }
    
    // Set theme-aware background
    const colors = getThemeColors();
    ctx.fillStyle = colors.backgroundColor;
    ctx.fillRect(0, 0, rect.width, rect.height);
    
    // Reset drawing styles
    ctx.strokeStyle = colors.strokeColor;
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    
    // Update state
    setSignatureData('');
    handleValueChange('');
  };


  // Update canvas colors when theme changes
  const updateCanvasColors = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    const colors = getThemeColors();
    
    // Update stroke color
    ctx.strokeStyle = colors.strokeColor;
    
    // If canvas is empty, update background color
    if (!signatureData) {
      const rect = canvas.getBoundingClientRect();
      ctx.fillStyle = colors.backgroundColor;
      ctx.fillRect(0, 0, rect.width, rect.height);
    } else {
      // If there's signature data, we need to redraw with new background
      // Save the current signature data
      const currentSignature = signatureData;
      
      // Clear and redraw with new background
      const rect = canvas.getBoundingClientRect();
      ctx.fillStyle = colors.backgroundColor;
      ctx.fillRect(0, 0, rect.width, rect.height);
      
      // Reload the signature with new background
      if (currentSignature) {
        loadSignature(currentSignature);
      }
    }
  }, [getThemeColors, signatureData, loadSignature]);

  // Listen for theme changes and update canvas colors
  useEffect(() => {
    const handleThemeChange = () => {
      updateCanvasColors();
    };

    // Listen for class changes on document element
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && 
            (mutation.attributeName === 'class' || mutation.attributeName === 'data-theme')) {
          handleThemeChange();
        }
      });
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class', 'data-theme']
    });

    return () => {
      observer.disconnect();
    };
  }, [updateCanvasColors]);

  // Handle focus
  const handleFocus = () => {
    if (onFocus) {
      onFocus();
    }
  };

  // Handle blur
  const handleBlur = () => {
    
    if (onBlur) {
      onBlur();
    }
  };

  // Update touched fields
  useEffect(() => {
    if (isTouched) {
      touchedFields[id] = true;
    }
  }, [isTouched, id, touchedFields]);

  // Update value when formValue prop changes (but don't override user input)
  useEffect(() => {
    // Only update if the formValue is different and we're not currently drawing or saving
    // Also, don't reset if the user has drawn something (signatureData is not empty)
    // CRITICAL FIX: Don't reload signature if we're currently saving (prevents flicker)
    if (formValue !== signatureData && !isDrawing && !isSaving && (signatureData === '' || formValue !== '')) {
      // Additional check: don't reload if the formValue is the same as current signatureData
      if (formValue === signatureData) {
        return;
      }
      setSignatureData(formValue);
      if (formValue) {
        loadSignature(formValue);
      }
    } else if (formValue === '' && signatureData !== '' && !isDrawing && !isSaving) {
    } else if (isSaving) {
    }
  }, [formValue, signatureData, isDrawing, isSaving]);

  // Mark as touched when form is submitted
  useEffect(() => {
    if (formSubmitted) {
      setIsTouched(true);
    }
  }, [formSubmitted]);

  // Clear signature when switching away from test mode
  useEffect(() => {
    if (mode !== 'test') {
      setSignatureData('');
      // Clear the canvas as well
      const canvas = canvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext('2d');
        if (ctx) {
          const rect = canvas.getBoundingClientRect();
          canvas.width = rect.width;
          canvas.height = rect.height;
          const colors = getThemeColors();
          ctx.fillStyle = colors.backgroundColor;
          ctx.fillRect(0, 0, rect.width, rect.height);
        }
      }
    }
  }, [mode]);

  // Initialize canvas on mount and when mode changes
  useEffect(() => {
    initializeCanvas();
  }, [mode]); // Only reinitialize when mode changes, not when signatureData changes

  // Get label alignment class
  const getLabelAlignmentClass = (): string => {
    const alignment = properties?.styles?.labelAlignment;
    return alignment === 'left' ? 'label-left' : 'label-top';
  };

  // Get signature classes
  const getSignatureClasses = (): string => {
    const base = 'form-signature';
    const validation = validateField(signatureData);
    const error = !validation.isValid && isTouched ? 'is-invalid' : '';
    return `${base} ${error} ${className}`.trim();
  };

  const shouldDisable = disabled || readonly || properties?.validation?.readonly;

  return (
    <div className={`form-group ${getLabelAlignmentClass()}`}>
      {!hideLabel && properties.basic.label && (
        <label htmlFor={id} className="form-label">
          {properties.basic.label}
          {properties.validation.required && (
            <span className="required-indicator">*</span>
          )}
        </label>
      )}

      <div className="input-wrapper">
        <div className="signature-container" style={{ 
          position: 'relative',
          width: '100%',
          maxWidth: '100%',
          overflow: 'hidden'
        }}>
          <canvas
            ref={canvasRef}
            id={id}
            className={getSignatureClasses()}
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={stopDrawing}
            onFocus={handleFocus}
            onBlur={handleBlur}
             style={{
               width: '100%',
               height: '150px',
               border: '2px solid var(--df-color-primary-border)',
               borderRadius: '6px',
               cursor: shouldDisable ? 'not-allowed' : 'crosshair',
               backgroundColor: 'var(--df-color-fb-input-bg)',
               touchAction: 'none', // Prevent scrolling when drawing on mobile
               userSelect: 'none' // Prevent text selection
             }}
          />
          
          {!shouldDisable && (
            <button
              type="button"
              onClick={(e) => clearSignature(e)}
              className="clear-signature-btn"
              style={{
                position: 'absolute',
                bottom: '8px',
                right: '8px',
                padding: '4px 8px',
                backgroundColor: 'var(--df-color-error-primary)',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.75rem',
                fontWeight: '500',
                zIndex: 10,
                transition: 'all 0.2s ease',
                minWidth: '60px',
                minHeight: '28px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--df-color-error-dark, #dc2626)';
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--df-color-error-primary)';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              Clear
            </button>
          )}
        </div>

        <DfFormErrorMsg
          validationErrors={validationErrors}
          fieldId={id}
          touchedFields={touchedFields}
          formSubmitted={formSubmitted}
          properties={properties}
          localValidation={validateField(signatureData)}
          isTouched={isTouched}
          mode={mode}
        />
      </div>
    </div>
  );
};

export default DfFormSignature;
